/**
 * 이상한 방 - 오디오 층의 공통 배관: 설정 저장소 · 첫 제스처
 *
 * `engine.ts`(효과음) · `bgm.ts`(배경음) · `haptics.ts`(진동) 셋이 같은 것을 각자 들고 있었다 -
 * `localStorage`를 감싸는 코드, 0~1로 자르는 코드, 첫 제스처로 치는 이벤트 목록과 그 구독.
 * 세 사본은 글자까지 같았지만, 사본이라는 사실만으로 갈라질 자리였다. 여기가 원본이다.
 *
 * ## 무엇을 여기 두지 않는가
 *
 * 저장 **키** · **기본값** · **깨진 값의 해석**은 각 모듈에 남는다. 그것은 배관이 아니라 정책이다 -
 * 소리는 켜고 진동만 끄는 선택이 가능하려면 키가 갈라져 있어야 하고, 볼륨의 기본값은 효과음과
 * 배경음이 다르다(배경은 낮아야 한다). 여기는 "던지지 않는다"만 책임진다.
 *
 * ## 던지지 않는다
 *
 * `localStorage`는 **접근하는 것만으로 던진다**(사파리 프라이빗, 쿠키 차단, 서드파티 iframe).
 * 설정 하나 못 읽었다고 게임이 안 켜질 수는 없으므로 읽기·쓰기 모두 조용히 실패한다.
 * 오디오 층 전체의 규약이다 - 소리는 부가 기능이지 전제가 아니다.
 */

/* --- 설정 저장소 ------------------------------------------------------------------ */

type SettingsStorage = Pick<Storage, 'getItem' | 'setItem'>

/** 전역에서 `localStorage`를 꺼낸다. 속성 접근 자체가 던지는 환경이 있어 접근째로 감싼다. */
function storage(): SettingsStorage | null {
  try {
    return (globalThis as { localStorage?: SettingsStorage }).localStorage ?? null
  } catch {
    return null
  }
}

/**
 * 키 하나를 읽어 JSON으로 푼다. 없거나, 못 읽거나, JSON이 아니면 `null`.
 *
 * 돌려주는 값은 `unknown`이다 - 모양 검사(불리언인가, 배열인가)는 부른 모듈의 몫이다.
 * 저장된 문자열이 `'null'`이면 결과도 `null`인데, 호출부에서는 "없음"과 같은 결론(기본값)이라 구분하지 않는다.
 */
export function readJsonSetting(key: string): unknown {
  try {
    const raw = storage()?.getItem(key)
    if (raw === null || raw === undefined) return null
    return JSON.parse(raw) as unknown
  } catch {
    // 깨진 값이든 막힌 저장소든 결론은 같다: 없는 것으로 친다.
    return null
  }
}

/** 키 하나에 JSON으로 쓴다. 저장하지 못해도 이번 세션의 선택은 살아 있으므로 조용히 넘긴다. */
export function writeJsonSetting(key: string, value: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(value))
  } catch {
    // 용량 초과·접근 차단. 던지지 않는다.
  }
}

/** 0~1로 자른다. 숫자로 읽을 수 없으면 `fallback`. 볼륨처럼 단위 구간에 사는 설정값용이다. */
export function clampUnit(value: unknown, fallback: number): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(1, Math.max(0, n))
}

/* --- 첫 제스처 -------------------------------------------------------------------- */

/** 첫 제스처로 인정하는 이벤트. 폰(터치)·PC(클릭)·키보드를 모두 덮는다. */
export const UNLOCK_EVENTS: readonly string[] = ['pointerdown', 'touchend', 'keydown']

/**
 * 첫 제스처 한 번에 `onFirst`를 부르고 구독을 스스로 걷는다. 돌려주는 함수는 언제든 구독을 끊는다.
 *
 * 대상을 넘기지 않으면 `document`, 그것도 없으면 `globalThis`다. `addEventListener`가 없는
 * 실행기(순수 Node·워커)에서는 아무것도 걸지 않고 빈 해제 함수를 돌려준다 - 그런 곳에는
 * 스피커도 진동 장치도 없다.
 */
export function installGestureOnce(target: EventTarget | undefined, onFirst: () => void): () => void {
  const scope = target ?? (globalThis as { document?: EventTarget }).document ?? globalThis
  if (typeof scope.addEventListener !== 'function') return () => undefined

  const handler = (): void => {
    onFirst()
    remove()
  }
  const remove = (): void => {
    for (const name of UNLOCK_EVENTS) scope.removeEventListener(name, handler)
  }

  for (const name of UNLOCK_EVENTS) scope.addEventListener(name, handler)
  return remove
}
